import uuid from 'react-uuid';

import type { ChartT } from '@/entities/chart/api/types';
import {
  CHART_CALCULATION,
  CHART_GRAPH,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import type { ChartSeriesDef } from '@/entities/chart/model/types';
import {
  createComboChartSeries,
  createDefaultCrossAxisSeries,
  createDefaultSingleAxisSeries,
} from '@/features/(charts)/chart-controller/hooks/use-chart-state-action/chartStateHelpers';
import { ChartDetailPreprocess } from '@/features/(charts)/chart-renderer/lib/ChartPreprocessFactory/ChartPreprocessFactory';
import { AnalysisState } from '@/features/Analysis/types';
import { Dashboard2ColumnType, type Dashboard2T } from '@/features/Dashboard/types';
import { popup } from '@/stores/pure/PopupStore';

import type { ReportCreatePayloadDTO } from '../api/types';
import type { ChartInReport, MappedReportT, ReportItem, ReportT } from '../types';

const getFallbackSeries = (chart: ChartT): ChartSeriesDef[] => {
  if (chart.series?.length) {
    return chart.series;
  }

  if (chart.graph === CHART_GRAPH.COMBO_CHART) {
    return createComboChartSeries([]);
  }

  if (chart.type === CHART_TYPE.CROSS_AXIS) {
    return [createDefaultCrossAxisSeries()];
  }

  return [
    createDefaultSingleAxisSeries({
      measure: {
        metric: CHART_METRIC.FEEDBACK,
        calculation: CHART_CALCULATION.COUNT,
      },
      options: {
        view_type: CHART_METRIC_VIEW_TYPE.COUNT,
      },
    }),
  ];
};

export const reportUtils = {
  buildReport: (dashboard: Dashboard2T): ReportCreatePayloadDTO => {
    const items: (ReportItem & {
      row_id: string;
      column_id: string;
    })[] = [];
    for (const row of dashboard.rows || []) {
      for (const column of row.columns) {
        const type = column.type;
        const column_id = column.id;
        if (!column_id) throw new Error('Column ID is required');
        switch (type) {
          case Dashboard2ColumnType.DASHBOARD_COLUMN_CHART: {
            const targetChart = dashboard.charts?.find(
              (chart) => chart.alias === column.chart,
            );
            if (!targetChart) throw new Error('Chart not found');
            const chart = new ChartDetailPreprocess(
              {
                ...targetChart,
                series: getFallbackSeries(targetChart),
              },
            ).runPreprocess();
            const reportChart: ChartInReport = {
              ...targetChart,
              ...chart,
              alias: targetChart.alias,
            };
            items.push({
              alias: uuid(),
              type: Dashboard2ColumnType.DASHBOARD_COLUMN_CHART,
              chart: reportChart,
              row_id: row.id,
              column_id: column_id,
            });
            break;
          }
          case Dashboard2ColumnType.DASHBOARD_COLUMN_ANALYSIS: {
            const analysis = dashboard.analyses?.find(
              (analysis) => analysis.alias === column.analysis_id,
            );
            if (!analysis) throw new Error('Smart brief not found');

            if (analysis.state !== AnalysisState.OK) {
              popup({
                popupTheme: 'negative',
                title: 'Smart brief is not completed',
                message: 'Please wait until the smart brief is completed',
                type: 'alert',
              });
              break;
            }

            items.push({
              alias: uuid(),
              type: Dashboard2ColumnType.DASHBOARD_COLUMN_ANALYSIS,
              analysis: analysis,
              row_id: row.id,
              column_id: column_id,
            });
            break;
          }
          case Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX:
            items.push({
              alias: uuid(),
              type: Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX,
              text: column.text,
              row_id: row.id,
              column_id: column_id,
            });
            break;
          case Dashboard2ColumnType.DASHBOARD_COLUMN_FEEDBACK_GALLERY:
            items.push({
              alias: uuid(),
              type: Dashboard2ColumnType.DASHBOARD_COLUMN_FEEDBACK_GALLERY,
              feedbacks: column.feedbacks_setting,
              row_id: row.id,
              column_id: column_id,
            });
            break;
          default:
            throw new Error('Invalid column type');
        }
      }
    }

    return {
      dashboard_id: dashboard.alias,
      title: dashboard.title,
      items: items.map((item) => {
        return {
          alias: item.alias,
          type: item.type,
          chart: item.chart,
          analysis: item.analysis,
          text: item.text,
          feedbacks: item.feedbacks,
        };
      }),
      rows: dashboard.rows?.map((row) => ({
        alias: row.id,
        columns: row.columns.map((column) => {
          const column_id = column.id;
          if (!column_id) throw new Error('Column ID is required');
          const item = items.find((item) => item.column_id === column_id);
          if (!item) throw new Error('Item not found');
          return {
            alias: column_id,
            item_alias: item.alias,
          };
        }),
      })) ?? [],
    };
  },
  mapReport: (report: ReportT): MappedReportT => {
    return {
      ...report,
      mapped_rows:
        report.rows?.map((row, rowIndex) => ({
          ...row,
          mapped_columns: row.columns.map((column) => {
            const item = report.items?.find(
              (item) => item.alias === column.item_alias,
            );
            if (!item)
              throw new Error(
                `[mapReport] Item not found : ${column.item_alias}`,
              );
            return {
              ...column,
              row_alias: row.alias,
              row_index: rowIndex,
              item,
            };
          }),
        })) || [],
    };
  },
};
